import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";

import { setActiveReportKey } from "../../../redux/comparativeDashSlice";
import { COMPARATIVE_CARD_CONFIG } from "../comparativeCardConfig";
import ComparativeCard from "../components/ComparativeCard"; 
import ComparativeTable from "../components/ComparativeTable";
import SalesPurchaseCumulative from "../components/SalesPurchaseCumulative";
import BranchWiseCumulative from "../components/BranchWiseCumulative";
import { useComparativeData } from "../hooks/useComparativeData";
import BranchWiseAgGridTable from "../components/BranchWiseAgGridTable";
// import BreakdownAnalysisHeader from "../components/BreakdownAnalysisHeader";
import useFetch from "../../../hooks/useFetch";
import ItemGroupWiseTable from "../components/Itemgroupwisetable";
import AccountGroupWiseTable from "../components/accountgroupwisetable";
// import UniversalMatrixTable from "../components/Universalmatrixtable";

interface Props {
  type: "sales" | "purchase";
}

const ComparativeDashboard: React.FC<Props> = ({ type }) => {
  const dispatch = useDispatch();
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [enableBranch, setEnableBranch] = useState(false);
  const  callFetch  = useFetch(); 


const { decryptedData } = useSelector((state: RootState) => state.authData);
const { CompCode, FinYear, Provider } = decryptedData || {};
const isProvider1 = Number(Provider) === 1;

const [doneSteps, setDoneSteps] = useState<string[]>([]);
const [freshSteps, setFreshSteps] = useState<string[]>([]);
   const [cumulativeMode,setCumulativeMode] = useState(0);

const markDone = useCallback((label: string) => {
  setDoneSteps(prev => [...prev, label]);
  setFreshSteps(prev => [...prev, label]);
  setTimeout(() => setFreshSteps(prev => prev.filter(l => l !== label)), 1500);
}, []);

const stepDone  = (label: string) => !isProvider1 || doneSteps.includes(label);
const stepFresh = (label: string) =>  isProvider1 && freshSteps.includes(label);

const enableBranchChecking = useCallback(async () => {
  const url = `Report/GetBranchEnableInBusy?CompCode=${CompCode}&FY=${FinYear}`;
  try {
    const { res, got }: any = await callFetch(url, "GET");
    if (res.status === 200 && got.Status === 1) {
      setEnableBranch(got.BranchEnable || false);
    }
  } catch (error) {
    console.error("Error loading branch enable:", error);
  }
}, [callFetch, CompCode, FinYear]);

useEffect(() => {
  if (CompCode && FinYear) {
    enableBranchChecking();
  }
}, [enableBranchChecking]);


  // ── 1. API DATA FETCHERS HOOKS ──────────────────────────────────────────
  const { fetchData: fetchSalesData } = useComparativeData({
    reportKey: "sales",
    endpoint: "Report/GetDashboardComparitiveData",
    extraParams: { VchType: "9",Cumulative:cumulativeMode },
  });

  const { fetchData: fetchPurchaseData } = useComparativeData({
    reportKey: "purchase",
    endpoint: "Report/GetDashboardComparitiveData",
    extraParams: { VchType: "2",Cumulative:cumulativeMode },
  });
 //---FOR  BRANCH  WISE   SALE  PURCHASE----------------
  const { fetchData: fetchSalesBranchWise } = useComparativeData({
    reportKey: "salesBranchWise",
    endpoint: "Report/GetDashboardComparitiveDataBW",
    extraParams: { VchType: "9",Cumulative:cumulativeMode },
  });

  const { fetchData: fetchPurchaseBranchWise } = useComparativeData({
    reportKey: "purchaseBranchWise",
    endpoint: "Report/GetDashboardComparitiveDataBW",
    extraParams: { VchType: "2" ,Cumulative:cumulativeMode},
  });


// 🔥 NEW ADDITION: HEAD-WISE DATA FETCHING HOOKS--------------
  const { fetchData: fetchHeadWiseSale } = useComparativeData({
    reportKey: "headWiseSale",
    endpoint: "Report/GetDashboardComparitiveDataSPHeadWise",
    extraParams: { VchType: "9" ,Cumulative:cumulativeMode}, // Sales ke liye VchType 9
  });

  const { fetchData: fetchHeadWisePurchase } = useComparativeData({
    reportKey: "headWisePurchase",
    endpoint: "Report/GetDashboardComparitiveDataSPHeadWise",
    extraParams: { VchType: "2",Cumulative:cumulativeMode }, // Purchase ke liye VchType 2
  });


  //----------FOR  ACCOUNT  CATEGORY  WISE -------------------
  const { fetchData: fetchAccountCategorySale } = useComparativeData({
    reportKey: "accountCategoryWiseSale",
    endpoint: "Report/GetDashboardComparitiveDataACW",
    extraParams: { VchType: "9",Cumulative:cumulativeMode }, // Sales ke liye VchType 9
  });

  const { fetchData: fetchAccountCategoryPurchase } = useComparativeData({
    reportKey: "accountCategoryWisePurchase",
    endpoint: "Report/GetDashboardComparitiveDataACW",
    extraParams: { VchType: "2",Cumulative:cumulativeMode }, // Purchase ke liye VchType 2
  });


    //----------FOR  ITEM  CATEGORY  WISE -------------------
  const { fetchData: fetchItemCategorySale } = useComparativeData({
    reportKey: "itemCategoryWiseSale",
    endpoint: "Report/GetDashboardComparitiveDataICW",
    extraParams: { VchType: "9",Cumulative:cumulativeMode }, // Sales ke liye VchType 9
  });

  const { fetchData: fetchItemCategoryPurchase } = useComparativeData({
    reportKey: "itemCategoryWisePurchase",
    endpoint: "Report/GetDashboardComparitiveDataICW",
    extraParams: { VchType: "2" ,Cumulative:cumulativeMode}, // Purchase ke liye VchType 2
  });


useEffect(() => {
  if (type === "sales") {
    fetchSalesData();
    fetchSalesBranchWise();
    fetchHeadWiseSale();
    fetchAccountCategorySale();
    fetchItemCategorySale();
  }
  if (type === "purchase") {
    fetchPurchaseData();
    fetchPurchaseBranchWise();
    fetchHeadWisePurchase();
    fetchAccountCategoryPurchase();
    fetchItemCategoryPurchase();
  }
}, [cumulativeMode, type]);



  // ── 2. REDUX LAYER EXTRACTOR ────────────────────────────────────────────
  const { activeReportKey, reportData, yr1Label, yr2Label, loading: reduxLoading  } = useSelector(
    (state: RootState) => state.comparativeDash
  );

  // ── 3. CRITICAL LIFECYCLE FIX: SINGLE SOURCE EFFECT ─────────────────────
  useEffect(() => {
    if (!type) return;

    dispatch(setActiveReportKey(type));

    const salesSteps = [
      { label: 'Main Data',        fn: fetchSalesData },
      { label: 'Branch Wise',      fn: fetchSalesBranchWise },
      { label: 'Head Wise',        fn: fetchHeadWiseSale },
      { label: 'Account Category', fn: fetchAccountCategorySale },
      { label: 'Item Category',    fn: fetchItemCategorySale },
    ];
    const purchaseSteps = [
      { label: 'Main Data',        fn: fetchPurchaseData },
      { label: 'Branch Wise',      fn: fetchPurchaseBranchWise },
      { label: 'Head Wise',        fn: fetchHeadWisePurchase },
      { label: 'Account Category', fn: fetchAccountCategoryPurchase },
      { label: 'Item Category',    fn: fetchItemCategoryPurchase },
    ];
    const steps = type === 'sales' ? salesSteps : purchaseSteps;

    if (isProvider1) {
      setDoneSteps([]);
      setFreshSteps([]);
      const run = async () => {
        for (let i = 0; i < steps.length; i++) {
          try { await steps[i].fn(); markDone(steps[i].label); } catch { /* continue */ }
          if (i < steps.length - 1) await new Promise<void>(r => setTimeout(r, 200));
        }
      };
      run();
    } else {
      steps.forEach(s => s.fn());
    }
  }, [type, dispatch, isProvider1]); // eslint-disable-line react-hooks/exhaustive-deps
  

  // Default active key check config guard wrapper
  useEffect(() => {
    if (!activeReportKey && COMPARATIVE_CARD_CONFIG.length > 0) {
      dispatch(setActiveReportKey(COMPARATIVE_CARD_CONFIG[0].reportKey));
    }
  }, [activeReportKey, dispatch]);

  // ── 4. CONDITIONAL RENDER SYNC ROUTE ────────────────────────────────────
  const filteredConfig = COMPARATIVE_CARD_CONFIG.filter((c) => c.reportKey === type);

  const heading = type === "sales" ? "Sales" : "Purchase";
  const tableData = type === "sales" ? reportData?.sales : reportData?.purchase;
  const branchWiseData = type === "sales" ? reportData?.salesBranchWise : reportData?.purchaseBranchWise;
  const headWiseData = type === "sales" ? reportData?.headWiseSale : reportData?.headWisePurchase;
  const accountCategoryWiseData = type === "sales" ? reportData?.accountCategoryWiseSale : reportData?.accountCategoryWisePurchase;
  const itemCategoryWiseData = type === "sales" ? reportData?.itemCategoryWiseSale : reportData?.itemCategoryWisePurchase;


  // ✅ PERFECT ATOMIC SYNCHRONIZATION GUARD:


// const isDataFullyLoaded = useMemo(() => {
//   if (!isProvider1 && reduxLoading) return false;
//   if (!tableData?.DataM || tableData.DataM.length === 0) return false;
//   return true;
// }, [tableData, reduxLoading, isProvider1]);


const isDataFullyLoaded =
  useMemo(() => {
    if (
      !isProvider1 &&
      reduxLoading
    ) {
      return false;
    }

    // Table Mode
    if (
      cumulativeMode === 0
    ) {
      return (
        tableData?.DataM
          ?.length > 0
      );
    }

    // Cumulative Mode — new format: array of { Particulars, CYData, LYData }
    if (Array.isArray(tableData) && tableData.length > 0 && tableData[0]?.LYData !== undefined) {
      return true;
    }
    // Old format: { DataCY, DataLY }
    return (
      tableData?.DataCY?.length > 0 &&
      tableData?.DataLY?.length > 0
    );
  }, [
    tableData,
    reduxLoading,
    isProvider1,
    cumulativeMode,
  ]);

  return (
    <>
    <style>{`
      @keyframes cdStepSlideIn {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes cdTickPop {
        0%   { opacity: 0; transform: scale(0.5); }
        50%  { opacity: 1; transform: scale(1.3); }
        70%  { transform: scale(1); }
        100% { opacity: 0; transform: scale(0.8); }
      }
      .cd-step-in  { animation: cdStepSlideIn 0.4s ease both; }
      .cd-step-tick {
        position: absolute; top: 10px; right: 10px; z-index: 20;
        width: 28px; height: 28px; border-radius: 50%;
        background: #16a34a; color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-size: 15px; font-weight: 700;
        box-shadow: 0 2px 8px rgba(22,163,74,0.5);
        animation: cdTickPop 1.5s ease forwards;
        pointer-events: none;
      }
    `}</style>
    <div className="page-wrapper">
      <div className="container-fluid py-3">

        {/* Header Breadcrumb Pathway */}
        <div className="mb-4">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item text-primary small fw-bold">DASHBOARD</li>
              <li className="breadcrumb-item active small" aria-current="page">COMPARATIVE ANALYSIS</li>
            </ol>
          </nav>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h4 className="fw-bold m-0 text-dark">
              {type === "sales" ? "Sales Performance Metrics" : "Purchase Performance Metrics"}
            </h4>
            <button
              onClick={() => setCumulativeMode(prev => prev === 0 ? 1 : 0)}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                background: "#e2e8f0",
                borderRadius: "24px",
                padding: "3px",
                border: "none",
                cursor: "pointer",
              }}
            >
              {/* sliding pill */}
              <span
                style={{
                  position: "absolute",
                  top: "3px",
                  left: cumulativeMode === 0 ? "3px" : "calc(50% - 1px)",
                  width: "calc(50% - 2px)",
                  height: "calc(100% - 6px)",
                  background: "#ffffff",
                  borderRadius: "20px",
                  boxShadow: "0 1px 4px rgba(15,23,42,0.15)",
                  transition: "left 0.22s cubic-bezier(.4,0,.2,1)",
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  position: "relative",
                  zIndex: 1,
                  padding: "5px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: cumulativeMode === 0 ? "#0f172a" : "#64748b",
                  transition: "color 0.18s",
                  whiteSpace: "nowrap",
                }}
              >
                Month
              </span>
              <span
                style={{
                  position: "relative",
                  zIndex: 1,
                  padding: "5px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: cumulativeMode === 1 ? "#0f172a" : "#64748b",
                  transition: "color 0.18s",
                  whiteSpace: "nowrap",
                }}
              >
                Cumulative
              </span>
            </button>
          </div>
        </div>

        {/* Dynamic Full Width Info Metric Card */}
        <div className="row g-3 mb-4">
          {filteredConfig.map((config: any) => (
            <div key={config.reportKey} className="col-12">
              <ComparativeCard
                config={config}
                yr1Label={yr1Label}
                yr2Label={yr2Label}
                tableRef={tableRef}
                autoFetch={activeReportKey === config.reportKey && !reportData[config.reportKey]}
                cumulativeMode={cumulativeMode}
              />
            </div>
          ))}
        </div>

        {/* Main Content Controlled Sync Area */}
        <div className="row g-3">
          <div className="col-12">
            <div ref={tableRef} className="mt-2" style={{ minHeight: "300px" }}>
              
              {/* ✅ THE BRAHMASTRA WRAPPER: Jab tak active tab aur target data 100% accurate match nahi hote, safe loader display par rahega */}
          {isDataFullyLoaded ? (
            <div className="animate__animated animate__fadeIn">

              {/* 1. Main — cumulative chart or monthly table */}
              {cumulativeMode === 1 ? (
                <SalesPurchaseCumulative
                  heading={heading}
                  rowData={tableData || []}
                  yr1Label={yr1Label}
                  yr2Label={yr2Label}
                />
              ) : (
                <ComparativeTable
                  heading={heading}
                  rowData={tableData?.DataM || []}
                  dataY={tableData?.DataY}
                  yr1Label={yr1Label}
                  yr2Label={yr2Label}
                />
              )}
                {/* <BreakdownAnalysisHeader /> */}
              {/* 2. Branch Wise */}
              {stepDone('Branch Wise') && enableBranch && branchWiseData && branchWiseData.length > 0 && (
                <div className={`mt-4 position-relative${stepFresh('Branch Wise') ? ' cd-step-in' : ''}`}>
                  {stepFresh('Branch Wise') && <span className="cd-step-tick">✓</span>}
                  {cumulativeMode === 1 ? (
                    <BranchWiseCumulative
                      heading={`Branch Wise ${heading}`}
                      rowData={branchWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                    />
                  ) : (
                    <BranchWiseAgGridTable
                      rowData={branchWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                      heading={`Branch Wise ${heading}`}
                    />
                  )}
                </div>
              )}

              {/* <BreakdownAnalysisHeader /> */}

              {/* 3. Head Wise */}
              {stepDone('Head Wise') && headWiseData && headWiseData.length > 0 && (
                <div className={`mt-4 position-relative${stepFresh('Head Wise') ? ' cd-step-in' : ''}`}>
                  {stepFresh('Head Wise') && <span className="cd-step-tick">✓</span>}
                  {cumulativeMode === 1 ? (
                    <BranchWiseCumulative
                      heading={`Head Wise ${heading}`}
                      rowData={headWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                    />
                  ) : (
                    <BranchWiseAgGridTable
                      rowData={headWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                      heading={`Head Wise ${heading}`}
                    />
                  )}
                </div>
              )}

              {/* 4. Account Category Wise */}
              {stepDone('Account Category') && accountCategoryWiseData && accountCategoryWiseData.length > 0 && (
                <div className={`mt-4 position-relative${stepFresh('Account Category') ? ' cd-step-in' : ''}`}>
                  {stepFresh('Account Category') && <span className="cd-step-tick">✓</span>}
                  {cumulativeMode === 1 ? (
                    <BranchWiseCumulative
                      heading={`Account Category Wise ${heading}`}
                      rowData={accountCategoryWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                    />
                  ) : (
                    <BranchWiseAgGridTable
                      rowData={accountCategoryWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                      heading={`Account Category Wise ${heading}`}
                    />
                  )}
                </div>
              )}

              {/* 5. Item Category Wise */}
              {stepDone('Item Category') && itemCategoryWiseData && itemCategoryWiseData.length > 0 && (
                <div className={`mt-4 position-relative${stepFresh('Item Category') ? ' cd-step-in' : ''}`}>
                  {stepFresh('Item Category') && <span className="cd-step-tick">✓</span>}
                  {cumulativeMode === 1 ? (
                    <BranchWiseCumulative
                      heading={`Item Category Wise ${heading}`}
                      rowData={itemCategoryWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                    />
                  ) : (
                    <BranchWiseAgGridTable
                      rowData={itemCategoryWiseData}
                      yr1Label={yr1Label}
                      yr2Label={yr2Label}
                      heading={`Item Category Wise ${heading}`}
                    />
                  )}
                </div>
              )}


            {/* ----for  Product Group  Wise */}
              {isDataFullyLoaded && stepDone('Item Category') && (
              <div className="mt-4">
                <ItemGroupWiseTable
                  heading={`Item Group Wise ${heading}`}
                  VchType={type === "sales" ? "9" : "2"}
                  yr1Label={yr1Label}
                  yr2Label={yr2Label}
                  isCumulative={cumulativeMode === 1}
                />
              </div>
            )}

            {/* ----for  Account Group  Wise */}
              {isDataFullyLoaded && stepDone('Item Category') && (
              <div className="mt-4">
                <AccountGroupWiseTable
                  heading={`Account Group Wise ${heading}`}
                  VchType={type === "sales" ? "9" : "2"}
                  yr1Label={yr1Label}
                  yr2Label={yr2Label}
                  isCumulative={cumulativeMode === 1}
                />
              </div>
            )}

            </div>
          ) : (
             cumulativeMode !== 1 ? ( <div className="d-flex flex-column align-items-center justify-content-center p-5 bg-white rounded shadow-sm border border-light" style={{ minHeight: "400px" }}>
              <div className={`spinner-grow ${type === "sales" ? "text-primary" : "text-success"} mb-3`} role="status">
                <span className="visually-hidden">Syncing Store Data...</span>
              </div>
              <p className="text-muted fw-medium m-0">Loading {heading} Performance & Matrix Structures...</p>
            </div>   ) : null
          )}

            </div>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default ComparativeDashboard;

